import React from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'rebass'
import { array, object, bool, string } from 'prop-types'
import Link from '../Link'
import SearchInput from './components/SearchInput'
import MagnifyingGlass from './components/MagnifyingGlass'
import IconArrowRight from './components/IconCareersArrowRight'
import LoadMoreButton from './components/LoadMoreButton'
import ReactResponsiveSelect from 'react-responsive-select'
import {
  ResponsiveSelectFlex,
  Arrow
} from './components/ResponsiveSelectStyled'
import createQueryStringManager from '../../../lib/url-querystring-manager'
import { Grid } from '../../utils/style'

const buildPath = require('../../../lib/career/build-path')
const CareersListItem = styled(Box)`
  position: relative;
  color: ${(props) => props.theme.colors.grey_op80};
  background-color: ${(props) => props.theme.colors.white};

  & + & {
    border-top: 2px solid ${(props) => props.theme.colors.grey_op10};
  }

  a,
  span {
    padding: 40px;
    padding-right: 80px;
    display: block;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.3s
      ${(props) => props.theme.ease.ease_out_expo};

    &:hover {
      background-color: ${(props) => props.theme.colors.blue_op20};

      div {
        color: ${(props) => props.theme.colors.blue};
      }
    }
  }
`

const CareersListIcon = styled(Grid)`
  position: absolute;
  right: 40px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
`

const CareersListText = styled(Box)`
  position: relative;
  font-size: ${(props) => props.theme.fontSizes[6]}px;
  font-weight: ${(props) => props.theme.weights[1]};
  color: ${(props) => props.theme.colors.grey_light};
  transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
`

const CareersListLocationText = styled(Box)`
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.blue};
  font-weight: ${(props) => props.theme.weights[1]};
`

const CareersListFilterBar = styled(Flex)`
  position: relative;
  border-bottom: 1px solid ${(props) => props.theme.colors.blue};
  background-color: ${(props) => props.theme.colors.white};
`

const CareersListContainer = styled(Box)`
  max-width: ${(props) => props.theme.content.max.width};
`
CareersListContainer.defaultProps = {
  mx: 'auto'
}

const CareersListBackground = styled(Box)`
  background-color: ${(props) => props.theme.colors.blue_light};
`

const SearchBar = styled(Box)`
  position: relative;
  width: 100%;
`

const CareersListItems = styled(Flex)`
  flex-direction: column;
`

const searchItems = (item, searchPhrase) =>
  [
    item.title,
    item.sell,
    item.jobDescription,
    item.candidateDescription,
    item.otherInfo,
    item.dealership && item.dealership.shortName
  ]
    .join(' ')
    .trim()
    .toLowerCase()
    .includes(searchPhrase.toLowerCase())

class ContentCareersList extends React.Component {
  constructor(...args) {
    super(...args)
    this.queryString = createQueryStringManager(
      typeof document === 'undefined' ? '' : document.location.search
    )
    this.state = {
      searchPhrase: this.queryString.get('searchPhrase'),
      location: this.queryString.get('location') || this.props.location
    }
    this.handleSearchChange = this.handleSearchChange.bind(this)
    this.handleLocationChange = this.handleLocationChange.bind(this)
    this.getQuery = this.getQuery.bind(this)
  }

  componentDidMount() {
    window.addEventListener('hashchange', this.getQuery)
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.getQuery)
  }

  getQuery() {
    this.queryString = createQueryStringManager(document.location.search)
    this.setQueryString()
  }

  setQueryString() {
    const queryString = this.queryString.toString()

    const newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      queryString
    window.history.pushState({ path: newurl }, '', newurl)

    this.setState({
      searchPhrase: this.queryString.get('searchPhrase'),
      location: this.queryString.get('location')
    })
  }

  handleLocationChange(selected) {
    if (selected.value === this.state.location) {
      return
    }

    this.queryString.set('location', selected.value)
    this.setQueryString()
  }

  handleSearchChange(e) {
    this.setState({
      searchPhrase: e.target.value
    })
    this.queryString.set('searchPhrase', e.target.value)
    this.setQueryString()
  }

  render() {
    if (!this.props.list || this.props.list.length === 0) return null
    let filteredItems = this.props.list
    if (this.state.searchPhrase) {
      filteredItems = filteredItems.filter((item) =>
        searchItems(item, this.state.searchPhrase)
      )
    }
    if (this.state.location) {
      filteredItems = filteredItems.filter(
        (item) =>
          item.dealership && item.dealership.slug === this.state.location
      )
    }
    return (
      <CareersListBackground py={140}>
        <CareersListContainer>
          <CareersListFilterBar>
            <ResponsiveSelectFlex>
              <ReactResponsiveSelect
                name="location"
                options={[
                  { text: 'All locations', value: null },
                  ...this.props.locations.map((location) => ({
                    text: location.shortName,
                    value: location.slug
                  }))
                ]}
                selectedValue={this.state.location}
                onChange={this.handleLocationChange}
                caretIcon={<Arrow open />}
              />
            </ResponsiveSelectFlex>
            <SearchBar>
              <MagnifyingGlass alt="Magnifying Glass" />
              <SearchInput
                autoComplete="off"
                placeholder="Search all jobs"
                aria-label="Careers search"
                onChange={this.handleSearchChange}
                value={this.state.searchPhrase}
              />
            </SearchBar>
          </CareersListFilterBar>
          <CareersListItems>
            {filteredItems.length === 0 ? (
              <CareersListItem>
                <span>No results found.</span>
              </CareersListItem>
            ) : (
              filteredItems.map((item, index) => (
                <CareersListItem key={`cli:${index}${item}`}>
                  <Link to={buildPath(item)}>
                    <CareersListLocationText>
                      {item.dealership ? item.dealership.shortName : 'Citygate'}
                    </CareersListLocationText>
                    <CareersListText>{item.title}</CareersListText>
                    <CareersListIcon>{this.props.icon}</CareersListIcon>
                  </Link>
                </CareersListItem>
              ))
            )}
            {this.props.more && <LoadMoreButton>Load more</LoadMoreButton>}
          </CareersListItems>
        </CareersListContainer>
      </CareersListBackground>
    )
  }
}

ContentCareersList.defaultProps = {
  list: [],
  icon: <IconArrowRight />,
  spacing: { t: 0, l: 0, b: 0, r: 0 },
  more: false,
  locations: []
}
ContentCareersList.propTypes = {
  list: array,
  locations: array,
  location: string,
  icon: object,
  more: bool
}
export default ContentCareersList
